import { db } from '../db/database';

type DatabaseCleanupOptions = {
  preserveUserData?: boolean;
  clearSpecificTables?: string[];
  logResults?: boolean;
};

export class IndexedDbCleanupService {
  private static readonly LOG_PREFIX = '[IndexedDB Cleanup]';

  static async clearAllUserData(options: DatabaseCleanupOptions = {}) {
    const { logResults = true, clearSpecificTables } = options;
    const results: Record<
      string,
      { success: boolean; error?: string; count?: number }
    > = {};

    try {
      const tablesToClear = clearSpecificTables || ['workOrders'];

      for (const tableName of tablesToClear) {
        results[tableName] = await this.clearTable(tableName, logResults);
      }

      return { success: true, results };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      if (logResults) {
        console.error(`${this.LOG_PREFIX} Failed to clear user data:`, error);
      }
      return { success: false, error: errorMessage, results };
    }
  }

  private static async clearTable(
    tableName: string,
    logResults: boolean = true
  ) {
    try {
      if (tableName !== 'workOrders') {
        const errorMessage = `Table ${tableName} not found in database`;
        return { success: false, error: errorMessage };
      }

      const table = db.workOrders;
      const count = await table.count();
      await table.clear();

      return { success: true, count };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      if (logResults) {
        console.error(
          `${this.LOG_PREFIX} Failed to clear table ${tableName}:`,
          error
        );
      }
      return { success: false, error: errorMessage };
    }
  }

  static async clearExpiredData(
    daysToKeep: number = 7,
    logResults: boolean = true
  ) {
    try {
      await db.clearOldData(daysToKeep);

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      if (logResults) {
        console.error(
          `${this.LOG_PREFIX} Failed to clear expired data:`,
          error
        );
      }
      return { success: false, error: errorMessage };
    }
  }

  static async clearUserSpecificData(
    userId: string,
    logResults: boolean = true
  ) {
    try {
      const results: Record<
        string,
        { success: boolean; error?: string; count?: number }
      > = {};

      results.workOrders = await this.clearUserWorkOrders(userId, logResults);

      return { success: true, results };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      if (logResults) {
        console.error(
          `${this.LOG_PREFIX} Failed to clear user-specific data:`,
          error
        );
      }
      return { success: false, error: errorMessage };
    }
  }

  static async clearAllCachedTablesForUser(userId: string) {
    const tables = [
      db.cachedWorkOrders,
      db.cachedWorkOrderDetails,
      db.cachedEquipment,
      db.cachedSpecifications,
      db.cachedCharges,
      db.cachedCalibrationRuntimes,
      db.cachedEventDocuments,
      db.downloadMetadata
    ] as const;

    let totalDeleted = 0;

    for (const table of tables) {
      try {
        // Delete rows belonging to the previous user
        const deleted = await table
          .filter(item => (item as Record<string, unknown>)._userId === userId)
          .delete();
        totalDeleted += deleted;

        // Clean up orphaned rows with no _userId
        const orphaned = await table
          .filter(
            item => (item as Record<string, unknown>)._userId === undefined
          )
          .delete();
        totalDeleted += orphaned;
      } catch (error) {
        console.error(
          `${this.LOG_PREFIX} Failed to clear ${table.name} for user ${userId}:`,
          error
        );
      }
    }

    // Also clear work orders for the user
    await this.clearUserWorkOrders(userId, false);

    return { success: true, totalDeleted };
  }

  private static async clearUserWorkOrders(
    userId: string,
    logResults: boolean = true
  ) {
    try {
      const workOrdersTable = db.workOrders;

      const workOrderCount = await workOrdersTable
        .where('_queryKey')
        .startsWith(`["workorders","search",{"userName":"${userId}"`)
        .count();

      await workOrdersTable
        .where('_queryKey')
        .startsWith(`["workorders","search",{"userName":"${userId}"`)
        .delete();

      return { success: true, count: workOrderCount };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      if (logResults) {
        console.error(
          `${this.LOG_PREFIX} Failed to clear work orders for user ${userId}:`,
          error
        );
      }
      return { success: false, error: errorMessage };
    }
  }

  static async getDatabaseStats(logResults: boolean = true) {
    try {
      const stats = {
        workOrders: await db.workOrders.count()
      };

      return { success: true, stats };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      if (logResults) {
        console.error(
          `${this.LOG_PREFIX} Failed to get database stats:`,
          error
        );
      }
      return { success: false, error: errorMessage };
    }
  }

  static async clearAllDatabases() {
    try {
      await db.delete();

      await new Promise(resolve => setTimeout(resolve, 100));

      if ('indexedDB' in window) {
        const databases = await indexedDB.databases();
        for (const dbInfo of databases) {
          if (dbInfo.name && dbInfo.name.includes('Scales')) {
            try {
              const deleteReq = indexedDB.deleteDatabase(dbInfo.name);
              await new Promise((resolve, reject) => {
                deleteReq.onsuccess = () => resolve(void 0);
                deleteReq.onerror = () => reject(deleteReq.error);
                deleteReq.onblocked = () => {
                  setTimeout(resolve, 1000);
                };
              });
            } catch (error) {
              console.error(error);
            }
          }
        }
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error(`${this.LOG_PREFIX} Failed to clear all databases:`, error);
      return { success: false, error: errorMessage };
    }
  }
}

export const clearIndexedDbOnLogout = async () => {
  try {
    const allDataResult = await IndexedDbCleanupService.clearAllUserData({
      logResults: false,
      clearSpecificTables: ['workOrders']
    });

    if (!allDataResult.success) {
      const deleteResult = await IndexedDbCleanupService.clearAllDatabases();
      return deleteResult;
    }

    return allDataResult;
  } catch (error) {
    console.error('Failed to clear IndexedDB on logout:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

export const emergencyClearAllData = async () => {
  return await IndexedDbCleanupService.clearAllDatabases();
};
